// frontend-public/src/pages/BookingPage.js
// ============================================================
// Online Web Booking — 公開訂位頁（Plan 090 / 086 Phase 1a.0）
// 路由：/r/:slug   單欄、由上往下捲動的一頁式表單（非雙欄、非精靈分頁）
//
//   餐廳資訊 → 訂位日期(月曆，今天起 3 個月) → 大人/小孩 → 時段 → 加購
//   → 姓名/電話 → LINE 通知(勾選) → 同意聲明 → 確認訂位 → 訂位成功卡
//
// 桌機：置中單欄卡片；手機：全寬。字級一律 ≥16px。LINE 綁定為 1a.1（LIFF）。
// ============================================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Button, Chip, IconButton, TextField, Link,
  Checkbox, FormControlLabel, Stack, CircularProgress, Alert, Paper, Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlaceIcon from '@mui/icons-material/Place';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LanguageIcon from '@mui/icons-material/Language';
import MapIcon from '@mui/icons-material/Map';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import bookingService from '../services/bookingService';
import TakeoutPanel from '../components/web-booking/TakeoutPanel';
import rebazLogoR from '../assets/rebaz-logo-r.png';
import rebazLogoWords from '../assets/rebaz-logo-words.png';

const GREEN = '#06C755';
const GREEN_D = '#05b34c';
const COMPANY = '捷思博科技股份有限公司';

// ---- 日期工具（UTC 錨定，避開時區換日）----
const pad2 = (n) => String(n).padStart(2, '0');
const todayTaipei = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Taipei' });
const addDaysISO = (iso, days) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};
const isoOf = (y, m, d) => `${y}-${pad2(m + 1)}-${pad2(d)}`;        // m: 0-11
const monthDaysCount = (y, m) => new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
const firstWeekday = (y, m) => new Date(Date.UTC(y, m, 1)).getUTCDay(); // 0=Sun
const WEEKDAYS = ['日', '一', '二', '三', '四', '五', '六'];
const niceDate = (iso, today) => {
  if (!iso) return '';
  const off = Math.round((new Date(`${iso}T00:00:00Z`) - new Date(`${today}T00:00:00Z`)) / 86400000);
  const [, m, d] = iso.split('-');
  const wd = WEEKDAYS[new Date(`${iso}T00:00:00Z`).getUTCDay()];
  const md = `${parseInt(m, 10)}/${parseInt(d, 10)}`;
  if (off === 0) return `今天 ${md}`;
  if (off === 1) return `明天 ${md}`;
  return `${md}（週${wd}）`;
};

// ---- 適用星期（T-059）----
// daysOfWeek 未設 / 空 / 滿 7 天 → 每天適用（既有店家沒有這個欄位，行為不變）
const DOW_LABEL = ['日', '一', '二', '三', '四', '五', '六'];
const DOW_ORDER = [1, 2, 3, 4, 5, 6, 0];
const dowOfDate = (date) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(date || ''));
  return m ? new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).getUTCDay() : null;
};
const isEveryDay = (w) => {
  const d = w && w.daysOfWeek;
  return !Array.isArray(d) || d.length === 0 || d.length >= 7;
};
const appliesOn = (w, date) => {
  if (isEveryDay(w)) return true;
  const dow = dowOfDate(date);
  return dow === null ? true : w.daysOfWeek.includes(dow);
};
const dowSummary = (ds) => {
  const inOrder = DOW_ORDER.filter((d) => ds.includes(d));
  const runs = []; let run = [inOrder[0]];
  for (let i = 1; i < inOrder.length; i += 1) {
    if (DOW_ORDER.indexOf(inOrder[i]) === DOW_ORDER.indexOf(inOrder[i - 1]) + 1) run.push(inOrder[i]);
    else { runs.push(run); run = [inOrder[i]]; }
  }
  runs.push(run);
  return runs.map((r) => (r.length >= 3
    ? `週${DOW_LABEL[r[0]]}～${DOW_LABEL[r[r.length - 1]]}`
    : r.map((d) => `週${DOW_LABEL[d]}`).join('、'))).join('、');
};

// 營業時間資訊列：每天適用的一行；有星期限定的（例如週六兩輪）另外分行標示
const formatHours = (bh) => {
  if (!Array.isArray(bh) || !bh.length) return null;
  const every = bh.filter(isEveryDay);
  const scoped = bh.filter((w) => !isEveryDay(w));
  const range = (w) => `${w.start}–${w.end}`;
  const lines = [];
  if (every.length) lines.push(every.map(range).join(' · '));
  const byDow = new Map();
  for (const w of scoped) {
    const k = dowSummary(w.daysOfWeek);
    if (!byDow.has(k)) byDow.set(k, []);
    byDow.get(k).push(w);
  }
  for (const [k, ws] of byDow) {
    const twoRounds = ws.filter((w) => w.seating).length >= 2 ? '（分兩輪）' : '';
    lines.push(`${k} ${ws.map(range).join(' · ')}${twoRounds}`);
  }
  return lines.join('\n');
};

// 找出某個日期＋時間所屬時段的提示文案（訂位成功卡用）。
// 邊界與 groupSlots／後端 findSeatingWindow 一致：先半開區間，沒中才回退含端點。
const noticeForSlot = (businessHours, date, time) => {
  const ws = (Array.isArray(businessHours) ? businessHours : []).filter((w) => w && appliesOn(w, date));
  const t = String(time || '').slice(0, 5);
  if (!t) return null;
  const hit = ws.find((w) => t >= w.start && t < w.end) || ws.find((w) => t >= w.start && t <= w.end);
  return (hit && hit.notice) || null;
};

// 時段依營業時段(window)分組（午餐/晚餐…），>1 具名 window 時才顯示分組標籤。
// T-059：帶入所選日期 → 只用當天適用的 window 分組，並把該輪的提示帶出去。
const groupSlots = (slots, businessHours, date = null) => {
  const windows = (Array.isArray(businessHours) ? businessHours : []).filter((w) => w && appliesOn(w, date));
  if (windows.filter((w) => w && w.name).length < 2) {
    // 單一時段時仍可能有提示（例如整天就是一輪）
    const only = windows.find((w) => w && w.notice);
    return [{ name: null, slots, notice: only ? only.notice : null }];
  }
  const groups = windows.map((w) => ({ name: w.name || '', start: w.start, end: w.end, notice: w.notice || null, slots: [] }));
  const other = { name: null, notice: null, slots: [] };
  for (const s of slots) {
    // 邊界用半開區間 [start, end)，與後端 findSeatingWindow 一致：
    // 19:00 屬於「19:00–21:00 第二輪」，不是「17:00–19:00 第一輪」。
    // 兩輪相接時 19:00 兩邊都符合含端點的條件，find 會取到先出現的第一輪 → 分組錯誤。
    let g = groups.find((w) => s.time >= w.start && s.time < w.end);
    // 沒有任何時段用半開區間收得下時才回退成含端點 ——
    // 單一時段（例如晚餐 17:00–19:00、最後收客就在 19:00）的最後一格仍要歸進該組。
    if (!g) g = groups.find((w) => s.time >= w.start && s.time <= w.end);
    (g || other).slots.push(s);
  }
  const out = groups.filter((g) => g.slots.length).map((g) => ({ name: g.name, slots: g.slots, notice: g.notice }));
  if (other.slots.length) out.push({ name: null, slots: other.slots, notice: null });
  return out;
};

const ERROR_MESSAGES = {
  FULL: '該時段已額滿，請選擇其他時間',
  CLOSED: '所選時間非營業時段',
  PARTY_SIZE: '人數超出可訂範圍',
  OUT_OF_WINDOW: '所選日期不在可訂範圍',
  BAD_PHONE: '電話格式不正確，請重新輸入',
  BAD_REQUEST: '請填寫完整資料',
  RATE_LIMITED: '操作太頻繁，請稍候再試',
  SERVER_ERROR: '系統忙線中，請稍後再試'
};

const Stepper = ({ value, min, max, onChange }) => (
  <Stack direction="row" alignItems="center" spacing={1.5}>
    <IconButton size="small" disabled={value <= min} onClick={() => onChange(value - 1)}
      sx={{ border: '1px solid', borderColor: 'divider' }}>
      <RemoveIcon fontSize="small" />
    </IconButton>
    <Typography sx={{ minWidth: 30, textAlign: 'center', fontSize: 20, fontWeight: 600 }}>{value}</Typography>
    <IconButton size="small" disabled={value >= max} onClick={() => onChange(value + 1)}
      sx={{ border: '1px solid', borderColor: 'divider' }}>
      <AddIcon fontSize="small" />
    </IconButton>
  </Stack>
);

// whiteSpace：營業時間可能多行（T-059 週六兩輪另起一行），此時傳 "pre-line" 保留換行。
// 多行時圖示改為靠上對齊，才不會浮在中間。
const InfoRow = ({ icon, text, whiteSpace }) => (
  <Stack direction="row" spacing={1} alignItems={whiteSpace === 'pre-line' ? 'flex-start' : 'center'} sx={{ color: 'text.secondary' }}>
    {React.cloneElement(icon, { sx: { fontSize: 19, mt: whiteSpace === 'pre-line' ? '3px' : 0 } })}
    <Typography sx={{ fontSize: 16, whiteSpace: whiteSpace || 'normal', lineHeight: 1.6 }}>{text}</Typography>
  </Stack>
);

// 可點連結列（官網 / Google Maps）——T-013
const InfoLinkRow = ({ icon, href, label }) => (
  <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
    {React.cloneElement(icon, { sx: { fontSize: 19 } })}
    <Link href={href} target="_blank" rel="noopener" sx={{ fontSize: 16, color: BRAND_BLUE }}>{label}</Link>
  </Stack>
);

const Label = ({ children, sx }) => (
  <Typography sx={{ fontWeight: 700, fontSize: 17, mb: 1, ...sx }}>{children}</Typography>
);

const BRAND_BLUE = '#145da0';

// 頂部 Rebaz 品牌 navbar（非 sticky，106 IG 式：往下捲即捲走，把空間讓給表單/選單）。
const TopBar = () => (
  <Box sx={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    px: { xs: 2, sm: 3 }, height: 52, bgcolor: '#fff',
    borderBottom: '1px solid', borderColor: 'divider'
  }}>
    <Link href="https://rebaz.ai" target="_blank" rel="noopener" underline="none"
      sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box component="img" src={rebazLogoR} alt="Rebaz" sx={{ height: 30, width: 'auto' }} />
      <Box component="img" src={rebazLogoWords} alt="Rebaz" sx={{ height: 18, width: 'auto' }} />
    </Link>
    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>線上訂位</Typography>
  </Box>
);

// 單欄可捲動外層。必須 module-level（不可定義在 BookingPage 內）——否則每次 render 換新
// 元件 identity → 子樹 remount → 打字時 TextField 失焦。
const Page = ({ children }) => (
  <Box sx={{ minHeight: '100vh', bgcolor: '#eef1f5' }}>
    <TopBar />
    <Box sx={{ py: { xs: 0, sm: 4 } }}>
      <Box sx={{
        // 不能 overflow:hidden — 會讓卡片變成（不可捲的）scroll container，
        // 內部所有 position:sticky（tab 列/分類欄/購物車 bar）失效（IG 式 sticky 靠 viewport）。
        // 圓角剪裁改由第一個子元素（封面圖）自行處理。
        width: '100%', maxWidth: 600, mx: 'auto', bgcolor: '#fff',
        borderRadius: { xs: 0, sm: 3 },
        boxShadow: { xs: 'none', sm: '0 8px 30px rgba(0,0,0,.08)' }, minHeight: { xs: '100vh', sm: 'auto' }
      }}>
        {children}
      </Box>
    </Box>
  </Box>
);

// 內用訂位｜外帶自取 segmented tab（黑白，Plan 106；enabled 才顯示；1.5× 加大）。
// IG 式 sticky：TopBar/封面/店家資訊捲走後，只留這條（餐廳顯示名稱＋切換）常駐 top:0。
const TakeoutTabBar = ({ name, tab, onChange }) => (
  <Box sx={{
    position: 'sticky', top: 0, zIndex: 9, bgcolor: '#fff',
    borderBottom: '1px solid', borderColor: 'divider',
    px: { xs: 2, sm: 3 }, minHeight: 76,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1
  }}>
    <Typography noWrap sx={{ fontWeight: 800, fontSize: 20, minWidth: 0 }}>{name}</Typography>
    <Box sx={{ display: 'flex', bgcolor: '#f1f5f9', borderRadius: 99, p: '5px', flexShrink: 0 }}>
      {[['dine', '內用訂位'], ['takeout', '外帶自取']].map(([key, label]) => {
        const on = tab === key;
        return (
          <Box key={key} onClick={() => onChange(key)}
            sx={{
              px: 2.5, py: 1, borderRadius: 99, fontSize: 19, fontWeight: 700, cursor: 'pointer',
              bgcolor: on ? '#0f172a' : 'transparent', color: on ? '#fff' : '#475569',
              transition: 'background .15s'
            }}>
            {label}
          </Box>
        );
      })}
    </Box>
  </Box>
);

const BookingPage = () => {
  const { slug } = useParams();
  const today = todayTaipei();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [info, setInfo] = useState(null);

  // 外帶 tab（Plan 106）：記在 querystring（?tab=takeout），店家可直接發外帶連結
  const [searchParams, setSearchParams] = useSearchParams();
  const takeoutEnabled = !!info?.takeout?.enabled;
  const tab = takeoutEnabled && searchParams.get('tab') === 'takeout' ? 'takeout' : 'dine';
  const setTab = (t) => setSearchParams(t === 'takeout' ? { tab: 'takeout' } : {}, { replace: true });
  // 外帶面板開過就保持掛載（display 切換）——切回內用再切回來，購物車不清空
  const [takeoutMounted, setTakeoutMounted] = useState(false);
  useEffect(() => { if (tab === 'takeout') setTakeoutMounted(true); }, [tab]);

  const [selectedDate, setSelectedDate] = useState(today);
  const [adults, setAdults] = useState(2);
  const [childrenCount, setChildrenCount] = useState(0);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [dayClosed, setDayClosed] = useState(false); // 整日被擋（後端 availability.closed）
  const [closedType, setClosedType] = useState(null);   // 'soldout'(客滿) | 'closed'(公休) | null
  const [soldoutMeals, setSoldoutMeals] = useState([]); // ['lunch'|'dinner'] 該日客滿的時段（非整日）
  const [slidePos, setSlidePos] = useState(1);       // 相簿位置（延伸軌道：1..N 為真實張，0/N+1 為首尾 clone）
  const [coverAnimate, setCoverAnimate] = useState(true); // 到 clone 後無動畫跳回真實張時暫關
  const coverTouchX = useRef(null);                  // 相簿滑動起點
  const [selectedTime, setSelectedTime] = useState(null);
  const [addonValues, setAddonValues] = useState({});
  const [selectedReqs, setSelectedReqs] = useState([]); // 特殊需求標記（多選）
  const [note, setNote] = useState('');                 // 備註（自由文字）
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');             // 選填，僅當餐廳開啟收集 Email 時顯示
  const [gender, setGender] = useState('ms');         // 稱謂：'ms' 小姐（預設）/ 'mr' 先生，寫入 customer_gender；不強制、不擋客人
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [confirmation, setConfirmation] = useState(null);
  const [manageEmail, setManageEmail] = useState('');       // T-015 成功頁：寄管理連結
  const [manageEmailSent, setManageEmailSent] = useState(false);
  const [manageCopied, setManageCopied] = useState(false);

  // 月曆檢視月份
  const [viewY, setViewY] = useState(() => parseInt(today.slice(0, 4), 10));
  const [viewM, setViewM] = useState(() => parseInt(today.slice(5, 7), 10) - 1);

  const partySize = adults + childrenCount;
  const maxParty = info?.config?.maxPartySize || 10;
  // Bug1：線上單桌自動配（不併桌）→ 可線上訂上限 = min(店家設定 maxPartySize, 最大開放單桌座位)。
  const maxTableSeats = info?.config?.maxTableSeats || 0; // 0 = 未知（不啟用此限制）
  const restaurantPhone = info?.restaurant?.phone || null;
  const effectiveMax = maxTableSeats > 0 ? Math.min(maxParty, maxTableSeats) : maxParty;
  // 店家允許人數 > 單桌容量 → 超過上限的大團需併桌（人工）→ 提示來電。
  const overTableNote = maxTableSeats > 0 && maxTableSeats < maxParty;
  const windowDays = info?.config?.bookingWindowDays || 90;
  const maxDate = addDaysISO(today, windowDays);
  // 頂部相簿（coverImages；向下相容：只有單張 coverImageUrl 時回退成 [它]）
  const coverImages = useMemo(() => {
    const arr = info?.restaurant?.coverImages;
    if (Array.isArray(arr) && arr.length) return arr;
    const single = info?.restaurant?.coverImageUrl;
    return single ? [single] : [];
  }, [info]);
  // 無縫循環相簿：延伸軌道 [clone(末), ...真實, clone(首)]；到 clone 後無動畫跳回對應真實張，
  // 讓最後一張往右順順接第一張、第一張往左接最後一張（不會 5→4→3→2→1 倒退）。
  const coverN = coverImages.length;
  const coverTrack = coverN > 1 ? [coverImages[coverN - 1], ...coverImages, coverImages[0]] : coverImages;
  const coverRealIdx = coverN > 1 ? (((slidePos - 1) % coverN) + coverN) % coverN : 0;
  const coverNext = () => { setCoverAnimate(true); setSlidePos((p) => p + 1); };
  const coverPrev = () => { setCoverAnimate(true); setSlidePos((p) => p - 1); };
  const coverGoTo = (i) => { setCoverAnimate(true); setSlidePos(i + 1); };
  const onCoverTransitionEnd = (e) => {
    if (e.propertyName && e.propertyName !== 'transform') return;
    if (slidePos >= coverN + 1) { setCoverAnimate(false); setSlidePos(1); }        // 過 clone(首) → 跳真實第一張
    else if (slidePos <= 0) { setCoverAnimate(false); setSlidePos(coverN); }        // 過 clone(末) → 跳真實最後一張
  };
  useEffect(() => {
    if (!coverAnimate) { const id = requestAnimationFrame(() => setCoverAnimate(true)); return () => cancelAnimationFrame(id); }
  }, [coverAnimate]);
  useEffect(() => { setSlidePos(coverN > 1 ? 1 : 0); }, [coverN]); // 相簿張數變動 → 回第一張
  // 加購「需提前 N 天預訂」：所選訂位日距今天數（今天=0）
  const daysUntil = useMemo(
    () => Math.round((new Date(`${selectedDate}T00:00:00Z`) - new Date(`${today}T00:00:00Z`)) / 86400000),
    [selectedDate, today]
  );
  const weeklyClosedDays = info?.weeklyClosedDays || []; // 每週固定公休（0=日..6=六）
  const closedDates = info?.closedDates || []; // 整日關閉的特定日期（特殊日期/節日例外「完全不接受」）→ 月曆劃掉不可點
  const curY = parseInt(today.slice(0, 4), 10);
  const curM = parseInt(today.slice(5, 7), 10) - 1;

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const data = await bookingService.getRestaurant(slug);
        if (alive) setInfo(data);
      } catch (err) {
        if (alive) setNotFound(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [slug]);

  // info 載入後：若目前選的日期整日關閉（固定公休或特殊日期），往後跳到第一個可訂日（最多找 30 天）
  useEffect(() => {
    if (!info) return;
    const wcd = info.weeklyClosedDays || [];
    const cds = info.closedDates || [];
    if (!wcd.length && !cds.length) return;
    const isClosed = (d) => wcd.includes(new Date(`${d}T00:00:00Z`).getUTCDay()) || cds.includes(d);
    let d = selectedDate;
    for (let i = 0; i < 30 && isClosed(d); i++) {
      d = addDaysISO(d, 1);
    }
    if (d !== selectedDate) setSelectedDate(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info]);

  // 分頁標題：帶入餐廳名（分享/切分頁時較好辨識），離開時還原
  useEffect(() => {
    const name = info?.restaurant?.name;
    document.title = name ? `${name} · 線上訂位` : 'Rebaz 線上訂位';
    return () => { document.title = 'Rebaz 線上訂位'; };
  }, [info]);

  // 訂位成功頁出現時捲回頂端。用即時捲動（非 smooth）在成功頁 render 後執行，
  // 避免 smooth 在原本很長的訂位表單上跑、又被 Map iframe 改變高度後的 scroll anchoring 打亂而停在底部。
  useEffect(() => {
    if (confirmation) window.scrollTo(0, 0);
  }, [confirmation]);

  const loadAvailability = useCallback(async (date, party) => {
    setSlotsLoading(true);
    setSelectedTime(null);
    try {
      const data = await bookingService.getAvailability(slug, date, party);
      setSlots(Array.isArray(data?.slots) ? data.slots : []);
      setDayClosed(!!data?.closed);
      setClosedType(data?.closedType || null);
      setSoldoutMeals(Array.isArray(data?.soldoutMeals) ? data.soldoutMeals : []);
    } catch (err) {
      setSlots([]);
      setDayClosed(false);
      setClosedType(null);
      setSoldoutMeals([]);
    } finally {
      setSlotsLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    if (info && !confirmation && tab !== 'takeout') loadAvailability(selectedDate, partySize);
  }, [info, confirmation, tab, selectedDate, partySize, loadAvailability]);

  const slotGroups = useMemo(() => groupSlots(slots, info?.businessHours, selectedDate), [slots, info, selectedDate]);

  const calendarCells = useMemo(() => {
    const lead = firstWeekday(viewY, viewM);
    const n = monthDaysCount(viewY, viewM);
    const cells = [];
    for (let i = 0; i < lead; i++) cells.push(null);
    for (let d = 1; d <= n; d++) cells.push(d);
    return cells;
  }, [viewY, viewM]);

  const prevDisabled = viewY < curY || (viewY === curY && viewM <= curM);
  const nm = viewM === 11 ? 0 : viewM + 1;
  const ny = viewM === 11 ? viewY + 1 : viewY;
  const nextDisabled = `${ny}-${pad2(nm + 1)}-01` > maxDate;
  const goPrev = () => { let m = viewM - 1, y = viewY; if (m < 0) { m = 11; y -= 1; } setViewM(m); setViewY(y); };
  const goNext = () => { let m = viewM + 1, y = viewY; if (m > 11) { m = 0; y += 1; } setViewM(m); setViewY(y); };

  const setAdultsSafe = (v) => setAdults(Math.max(1, Math.min(v, effectiveMax - childrenCount)));
  const setChildrenSafe = (v) => setChildrenCount(Math.max(0, Math.min(v, effectiveMax - adults)));

  const phoneDigits = (phone || '').replace(/\D/g, '');
  const canSubmit = !!selectedTime && name.trim().length > 0 && phoneDigits.length >= 8 && !submitting;

  // 加購值設定（各型別：counter 數字 / toggle 布林 / single_select 值 / text 文字 / multi_select 陣列）
  const setAddonNumber = (key) => (v) => setAddonValues((p) => ({ ...p, [key]: v }));
  const setAddonToggle = (key, checked) => setAddonValues((p) => ({ ...p, [key]: checked }));
  const setAddonSingle = (key, value) => setAddonValues((p) => ({ ...p, [key]: value }));
  const setAddonText = (key, value) => setAddonValues((p) => ({ ...p, [key]: value }));
  const toggleMulti = (key, value) => (e) => setAddonValues((p) => {
    const cur = Array.isArray(p[key]) ? p[key] : [];
    const next = e.target.checked ? [...new Set([...cur, value])] : cur.filter((v) => v !== value);
    return { ...p, [key]: next };
  });

  const toggleReq = (tag) =>
    setSelectedReqs((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));

  const handleSubmit = async () => {
    setSubmitError('');
    setSubmitting(true);
    try {
      const extras = {};
      (info.addons || []).forEach((a) => {
        // 需提前 N 天但所選日期太近 → 不送出（前端已灰掉，此為保險）
        if (Number.isInteger(a.leadDays) && a.leadDays > 0 && daysUntil < a.leadDays) return;
        const v = addonValues[a.key];
        if (a.type === 'counter') { const n = Number(v); if (n > 0) extras[a.key] = n; }
        else if (a.type === 'multi_select') { if (Array.isArray(v) && v.length) extras[a.key] = v; }
        else if (a.type === 'single_select') { if (v) extras[a.key] = v; }
        else if (a.type === 'text') { const s = String(v || '').trim(); if (s) extras[a.key] = s; }
        else if (v) { extras[a.key] = a.label || true; } // toggle
      });
      const data = await bookingService.createReservation(slug, {
        date: selectedDate, time: selectedTime, partySize, adults, children: childrenCount,
        name: name.trim(), phone: phoneDigits, gender, extras,
        specialRequests: selectedReqs, note: note.trim() || undefined,
        email: email.trim() || undefined
      });
      setConfirmation(data.reservation);
    } catch (err) {
      const body = err?.response?.data || {};
      const code = body.error;
      if (code === 'DUPLICATE') {
        // 同一時段已訂過同一筆
        const ex = body.existing;
        setSubmitError(ex ? `您在 ${ex.date} ${ex.time} 已有一筆訂位，請勿重複預訂` : '您已有相同時段的訂位，請勿重複預訂');
      } else if (code === 'LIMIT_REACHED') {
        // 未來有效訂位達上限（與「某時段重複」不同）
        const lim = body.limit || 3;
        setSubmitError(`同一組電話最多可保留 ${lim} 筆未來訂位，您目前已達上限。請先取消或修改現有訂位後再預訂。`);
      } else {
        setSubmitError(ERROR_MESSAGES[code] || '訂位失敗，請稍後再試');
        if (code === 'FULL') loadAvailability(selectedDate, partySize);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const resetAll = () => {
    setConfirmation(null);
    setSelectedTime(null);
    setAdults(2);
    setChildrenCount(0);
    setAddonValues({});
    setSelectedReqs([]);
    setNote('');
    setName('');
    setPhone('');
    setEmail('');
    setSubmitError('');
    setManageEmail('');
    setManageEmailSent(false);
    setManageCopied(false);
  };

  // 成功頁「管理我的訂位」（T-015）— 連結 / 複製 / 分享 / 寄 email
  const manageUrlOf = () => (confirmation && confirmation.cancelToken)
    ? `${window.location.origin}/c/${confirmation.cancelToken}` : '';
  const copyManage = () => {
    if (navigator.clipboard) navigator.clipboard.writeText(manageUrlOf()).catch(() => {});
    setManageCopied(true); setTimeout(() => setManageCopied(false), 1500);
  };
  const shareManage = () => {
    const d = { title: `${info.restaurant?.name || ''} 訂位`, text: '我的訂位管理連結', url: manageUrlOf() };
    if (navigator.share) navigator.share(d).catch(() => {});
    else if (navigator.clipboard) { navigator.clipboard.writeText(d.url); setManageCopied(true); setTimeout(() => setManageCopied(false), 1500); }
  };
  const sendManage = async () => {
    const em = (manageEmail || '').trim();
    if (!em || !confirmation || !confirmation.cancelToken) return;
    try { await bookingService.emailManageLink(confirmation.cancelToken, em); setManageEmailSent(true); }
    catch (e) { /* 靜默：後端 fire-and-forget，網路錯不誤導 */ }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#eef1f5' }}>
        <CircularProgress sx={{ color: GREEN }} />
      </Box>
    );
  }
  if (notFound) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#eef1f5', p: 3 }}>
        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 360, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>此餐廳尚未開放線上訂位</Typography>
          <Typography sx={{ fontSize: 16 }} color="text.secondary">請確認連結是否正確，或直接來電預訂。</Typography>
        </Paper>
      </Box>
    );
  }

  if (confirmation) {
    return (
      <Page>
        <Box sx={{ p: { xs: 3, sm: '40px 36px' } }}>
          <Box sx={{ textAlign: 'center', pt: 2, pb: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: GREEN }} />
            <Typography variant="h5" sx={{ mt: 1, fontWeight: 700 }}>訂位成功</Typography>
          </Box>
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{info.restaurant?.name}</Typography>
            {info.restaurant?.address && (
              <Typography sx={{ fontSize: 15, color: 'text.secondary', mb: 1.5 }}>{info.restaurant.address}</Typography>
            )}
            <Stack spacing={1}>
              <Row label="日期時間" value={`${niceDate(confirmation.date, today)} · ${confirmation.time}`} />
              <Row label="人數" value={`${confirmation.partySize} 位`} />
              <Row label="訂位人" value={`${name.trim()}${gender === 'ms' ? ' 小姐' : gender === 'mr' ? ' 先生' : ''}`} />
              <Row label="電話" value={phone.trim()} />
            </Stack>
            {/* 該輪的提示（T-059）—— 客人挑時間時看過一次，送出後這裡再提醒一次，
                否則兩輪制的「幾點要離席」他到現場才發現。 */}
            {noticeForSlot(info?.businessHours, confirmation.date, confirmation.time) && (
              <Box sx={{ mt: 2, pt: 1.75, borderTop: '1px solid', borderColor: 'divider',
                         display: 'flex', gap: 0.75, alignItems: 'flex-start' }}>
                <Typography sx={{ fontSize: 15, color: 'warning.main', lineHeight: 1.6 }}>ⓘ</Typography>
                <Typography sx={{ fontSize: 15, color: 'warning.main', lineHeight: 1.6 }}>
                  {noticeForSlot(info?.businessHours, confirmation.date, confirmation.time)}
                </Typography>
              </Box>
            )}
          </Paper>
          {/* 訂位成功後：加入餐廳官方 LINE 先點餐（後台可設連結+說明；enabled+url 才顯示）*/}
          {info.preorderLine?.enabled && info.preorderLine?.url && (
            <Box sx={{ mt: 2, border: '1px solid', borderColor: '#d6f2df', borderRadius: 2, p: 2.5, bgcolor: '#f2fbf5', textAlign: 'center' }}>
              {info.preorderLine.title && (
                <Typography sx={{ fontSize: 17, fontWeight: 700, mb: 0.8 }}>{info.preorderLine.title}</Typography>
              )}
              {info.preorderLine.description && (
                <Typography sx={{ fontSize: 16, lineHeight: 1.75, color: 'text.secondary', mb: 1.8, whiteSpace: 'pre-line', textAlign: 'left' }}>
                  {info.preorderLine.description}
                </Typography>
              )}
              <Button fullWidth variant="contained" component="a" href={info.preorderLine.url} target="_blank" rel="noopener"
                startIcon={<RestaurantMenuIcon />}
                sx={{ bgcolor: GREEN, '&:hover': { bgcolor: GREEN_D }, fontSize: 17, fontWeight: 700, py: 1.3 }}>
                加入官方帳號先點菜
              </Button>
            </Box>
          )}
          {/* 餐廳位置 Google Map 直接嵌入（有地址才顯示） */}
          {info.restaurant?.address && (
            <Box sx={{ mt: 2, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
              <Box component="iframe" title="餐廳位置地圖" loading="lazy"
                src={`https://www.google.com/maps?q=${encodeURIComponent(info.restaurant.address)}&output=embed`}
                sx={{ width: '100%', height: 200, border: 0, display: 'block' }} />
            </Box>
          )}
          {info.restaurant?.mapUrl && (
            <Button fullWidth variant="outlined" component="a" href={info.restaurant.mapUrl} target="_blank" rel="noopener"
              startIcon={<MapIcon />} sx={{ mt: 1.5, color: BRAND_BLUE, borderColor: BRAND_BLUE, fontSize: 16 }}>
              Google Maps 導航
            </Button>
          )}
          {/* 1a.1 LINE 綁定後此處改為「確認已發送到您的 LINE」 */}
          {confirmation.cancelToken ? (
            <Box sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: '#fbfcfe' }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700 }}>需要更改或取消？</Typography>
              <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1.4 }}>用這條專屬連結即可自助管理，免登入。</Typography>
              <Button fullWidth variant="contained" component="a" href={`/c/${confirmation.cancelToken}`} target="_blank" rel="noopener"
                sx={{ bgcolor: BRAND_BLUE, '&:hover': { bgcolor: BRAND_BLUE }, fontSize: 15 }}>🔗 管理我的訂位</Button>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, bgcolor: '#f4f6f9', border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, p: '6px 8px 6px 12px', mt: 1.4 }}>
                <Typography sx={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'text.secondary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{manageUrlOf().replace(/^https?:\/\//, '')}</Typography>
                <Button size="small" onClick={copyManage} sx={{ minWidth: 0, color: BRAND_BLUE, fontSize: 12.5, fontWeight: 700 }}>{manageCopied ? '已複製' : '複製'}</Button>
                <Button size="small" onClick={shareManage} sx={{ minWidth: 0, color: BRAND_BLUE, fontSize: 12.5, fontWeight: 700 }}>分享</Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mt: 1.4 }}>
                <TextField size="small" fullWidth type="email" placeholder="把管理連結寄到信箱" value={manageEmail} onChange={(e) => setManageEmail(e.target.value)} sx={{ '& .MuiInputBase-input': { fontSize: 14 } }} />
                <Button variant="contained" onClick={sendManage} disabled={manageEmailSent}
                  sx={{ bgcolor: BRAND_BLUE, '&:hover': { bgcolor: BRAND_BLUE }, whiteSpace: 'nowrap', px: 2 }}>{manageEmailSent ? '已寄出' : '寄送'}</Button>
              </Box>
              <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: 1 }}>建議加入書籤或收信保存，方便日後查看與修改。</Typography>
            </Box>
          ) : (
            <Alert severity="info" sx={{ mt: 2, borderRadius: 2, fontSize: 16 }}>訂位已成立。如需異動或取消，請來電與餐廳聯繫。</Alert>
          )}
          <Button fullWidth variant="text" onClick={resetAll} sx={{ mt: 2, color: GREEN, fontSize: 16 }}>再訂一筆</Button>
        </Box>
      </Page>
    );
  }

  return (
    <Page>
      {/* 餐廳照片相簿（後台可設多張；1 張＝同單封面，≥2 張可左右滑）*/}
      {coverImages.length > 0 && (
        <Box
          onTouchStart={(e) => { coverTouchX.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            if (coverTouchX.current == null || coverImages.length < 2) return;
            const dx = e.changedTouches[0].clientX - coverTouchX.current;
            if (Math.abs(dx) > 40) { if (dx < 0) coverNext(); else coverPrev(); }
            coverTouchX.current = null;
          }}
          sx={{
            position: 'relative', width: '100%', height: { xs: 180, sm: 230 }, overflow: 'hidden',
            bgcolor: '#e5e7eb', borderRadius: { xs: 0, sm: '12px 12px 0 0' }
          }}>
          <Box onTransitionEnd={onCoverTransitionEnd}
            sx={{ display: 'flex', height: '100%', transition: coverAnimate ? 'transform .3s ease' : 'none', transform: `translateX(-${slidePos * 100}%)` }}>
            {coverTrack.map((src, i) => (
              <Box key={i} sx={{ minWidth: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
                {/* 模糊背景填滿邊（方形菜色照不留黑邊）*/}
                <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `url(${src})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'blur(16px) brightness(.72)', transform: 'scale(1.2)' }} />
                {/* 完整照片，不裁切（cover→contain）*/}
                <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `url(${src})`, backgroundSize: 'contain', backgroundPosition: 'center', backgroundRepeat: 'no-repeat' }} />
              </Box>
            ))}
          </Box>
          {coverImages.length > 1 && (
            <>
              <Box sx={{ position: 'absolute', bottom: 10, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 0.7 }}>
                {coverImages.map((_, i) => (
                  <Box key={i} onClick={() => coverGoTo(i)}
                    sx={{ width: i === coverRealIdx ? 18 : 7, height: 7, borderRadius: 4, cursor: 'pointer', transition: 'width .2s',
                      bgcolor: i === coverRealIdx ? '#fff' : 'rgba(255,255,255,.6)' }} />
                ))}
              </Box>
              {/* 無縫循環：箭頭永遠都在，到底往前接第一張、到頭往前接最後一張 */}
              <IconButton size="small" onClick={coverPrev}
                sx={{ position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)', width: 32, height: 32,
                  bgcolor: 'rgba(0,0,0,.32)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,.5)' } }}><ChevronLeftIcon /></IconButton>
              <IconButton size="small" onClick={coverNext}
                sx={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 32, height: 32,
                  bgcolor: 'rgba(0,0,0,.32)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,.5)' } }}><ChevronRightIcon /></IconButton>
            </>
          )}
        </Box>
      )}
      {/* 餐廳資訊 */}
      <Box sx={{ p: { xs: '24px 22px 16px', sm: '30px 36px 18px' } }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>{info.restaurant?.name}</Typography>
        <Stack spacing={0.6} sx={{ mt: 1.2 }}>
          {formatHours(info.businessHours) && <InfoRow icon={<AccessTimeIcon />} text={formatHours(info.businessHours)} whiteSpace="pre-line" />}
          {info.restaurant?.address && <InfoRow icon={<PlaceIcon />} text={info.restaurant.address} />}
          {info.restaurant?.phone && <InfoRow icon={<PhoneIcon />} text={info.restaurant.phone} />}
          {info.restaurant?.website && <InfoLinkRow icon={<LanguageIcon />} href={info.restaurant.website} label="官方網站" />}
          {info.restaurant?.mapUrl && <InfoLinkRow icon={<MapIcon />} href={info.restaurant.mapUrl} label="Google Maps 導航" />}
        </Stack>
      </Box>
      <Divider />

      {/* 內用訂位｜外帶自取 tab（Plan 106；takeout.enabled 才顯示，訂位側原封不動） */}
      {takeoutEnabled && <TakeoutTabBar name={info.restaurant?.name} tab={tab} onChange={setTab} />}

      {/* 外帶面板：開過就保持掛載，切 tab 用 display 切換（保留購物車） */}
      {takeoutMounted && (
        <Box sx={{ display: tab === 'takeout' ? 'block' : 'none' }}>
          <TakeoutPanel slug={slug} restaurant={info.restaurant} />
        </Box>
      )}

      <Box sx={{ display: tab === 'takeout' ? 'none' : 'block', p: { xs: 3, sm: '24px 36px 40px' } }}>
        {/* 訂位日期 — 月曆，今天起 3 個月 */}
        <Label>訂位日期</Label>
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.5, mb: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Button size="small" disabled={prevDisabled} onClick={goPrev} startIcon={<ChevronLeftIcon />}
              sx={{ color: 'text.primary', fontSize: 16, textTransform: 'none', minWidth: 0, px: 0.5 }}>上個月</Button>
            <Typography sx={{ fontWeight: 700, fontSize: 17 }}>{viewY} 年 {viewM + 1} 月</Typography>
            <Button size="small" disabled={nextDisabled} onClick={goNext} endIcon={<ChevronRightIcon />}
              sx={{ color: 'text.primary', fontSize: 16, textTransform: 'none', minWidth: 0, px: 0.5 }}>下個月</Button>
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center', mb: 0.5 }}>
            {WEEKDAYS.map((w) => <Typography key={w} sx={{ fontSize: 16 }} color="text.secondary">{w}</Typography>)}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
            {calendarCells.map((cell, i) => {
              if (cell === null) return <Box key={`e${i}`} sx={{ height: 40 }} />;
              const iso = isoOf(viewY, viewM, cell);
              const dow = new Date(`${iso}T00:00:00Z`).getUTCDay();
              const closedDay = weeklyClosedDays.includes(dow) || closedDates.includes(iso); // 固定公休 + 特殊日期整日關閉 → 不可選、劃線
              const selectable = iso >= today && iso <= maxDate && !closedDay;
              const sel = iso === selectedDate && selectable;
              return (
                <Box key={iso}
                  onClick={() => selectable && setSelectedDate(iso)}
                  sx={{
                    height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    borderRadius: 2, fontSize: 16, cursor: selectable ? 'pointer' : 'default',
                    color: sel ? '#fff' : (selectable ? 'text.primary' : 'text.disabled'),
                    textDecoration: closedDay ? 'line-through' : 'none',  // 公休日劃線
                    bgcolor: sel ? GREEN : 'transparent',
                    '&:hover': selectable && !sel ? { bgcolor: '#f0f0f0' } : {}
                  }}>
                  {cell}
                </Box>
              );
            })}
          </Box>
          {weeklyClosedDays.length > 0 && (
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 1 }}>
              每週{weeklyClosedDays.map((d) => `週${WEEKDAYS[d]}`).join('、')}公休
            </Typography>
          )}
        </Paper>

        {/* 用餐人數 — 大人 / 小孩 直接在這頁 */}
        <Label>用餐人數</Label>
        <Stack spacing={1.5} sx={{ mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography sx={{ fontSize: 16 }}>大人</Typography>
              <Typography sx={{ fontSize: 16 }} color="text.secondary">12 歲以上</Typography>
            </Box>
            <Stepper value={adults} min={1} max={effectiveMax - childrenCount} onChange={setAdultsSafe} />
          </Stack>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography sx={{ fontSize: 16 }}>小孩</Typography>
              <Typography sx={{ fontSize: 16 }} color="text.secondary">12 歲以下</Typography>
            </Box>
            <Stepper value={childrenCount} min={0} max={effectiveMax - adults} onChange={setChildrenSafe} />
          </Stack>
          <Typography sx={{ fontSize: 16 }} color="text.secondary">共 {partySize} 位（每筆最多 {effectiveMax} 位）</Typography>
          {overTableNote && (
            <Box sx={{
              bgcolor: 'rgba(6,199,85,0.10)', border: `1px solid ${GREEN}`, borderRadius: 1.5, px: 1.5, py: 1.2, alignSelf: 'stretch'
            }}>
              <Typography sx={{ fontSize: 17, fontWeight: 700, color: GREEN_D, lineHeight: 1.4 }}>
                超過 {effectiveMax} 位無法線上訂位
              </Typography>
              <Typography sx={{ fontSize: 15, color: 'text.secondary', mt: 0.3 }}>
                請來電由專人為您安排併桌
              </Typography>
              {restaurantPhone && (
                <Box component="a" href={`tel:${restaurantPhone}`}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.6, mt: 0.8,
                    fontSize: 22, fontWeight: 800, color: GREEN, textDecoration: 'none'
                  }}>
                  <PhoneIcon sx={{ fontSize: 22 }} /> {restaurantPhone}
                </Box>
              )}
            </Box>
          )}
        </Stack>

        {/* 時段 */}
        <Label>時段</Label>
        {slotsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} sx={{ color: GREEN }} /></Box>
        ) : dayClosed ? (
          // 整日被擋：客滿(soldout) → 本日已訂滿；公休(closed) → 本日公休。
          <Alert severity="info" sx={{ borderRadius: 2, fontSize: 16 }}>
            {closedType === 'soldout' ? '本日已訂滿，請改選其他日期' : '本日公休，請另選日期'}
          </Alert>
        ) : slots.length === 0 ? (
          <Typography sx={{ fontSize: 16, py: 2 }} color="text.secondary">此日期暫無可訂時段</Typography>
        ) : slots.every((s) => !s.available) ? (
          // 當天沒公休、非客滿全天，但此人數在所有時段都配不到桌（線上不併桌，最大單桌 < 人數）或整日訂滿。
          <Alert severity="info" sx={{ borderRadius: 2, fontSize: 16 }}>本日此人數暫無可訂時段，請調整人數或改選日期</Alert>
        ) : (
          <>
            {soldoutMeals.length > 0 && (
              // 部分時段客滿（午/晚）：其餘時段仍可訂，額滿時段的時段鈕會 disabled/劃線。
              <Alert severity="info" sx={{ borderRadius: 2, fontSize: 15, mb: 1.5 }}>
                本日{soldoutMeals.map((m) => (m === 'lunch' ? '午餐' : '晚餐')).join('、')}時段已訂滿，可改選其他時段
              </Alert>
            )}
          {slotGroups.map((g, gi) => (
            <Box key={g.name || `g${gi}`} sx={{ mb: 1.5 }}>
              {g.name && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: gi ? 2 : 0, mb: 1 }}>
                  <Typography sx={{ fontSize: 16 }} color="text.secondary">{g.name}</Typography>
                  <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
                </Box>
              )}
              {/* 該輪的提示（T-059）— 例「本輪用餐至 19:00，敬請配合」 */}
              {g.notice && (
                <Typography sx={{ fontSize: 12.5, color: 'warning.main', mb: 1, lineHeight: 1.6 }}>
                  ⓘ {g.notice}
                </Typography>
              )}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(4, 1fr)', sm: 'repeat(6, 1fr)' }, gap: 1 }}>
                {g.slots.map((s) => {
                  const active = s.time === selectedTime;
                  return (
                    <Chip key={s.time} label={s.time} disabled={!s.available}
                      onClick={() => s.available && setSelectedTime(s.time)}
                      sx={{
                        height: 46, borderRadius: 2, fontSize: 16,
                        textDecoration: s.available ? 'none' : 'line-through',
                        bgcolor: active ? GREEN : '#fff', color: active ? '#fff' : 'text.primary',
                        border: '1px solid', borderColor: active ? GREEN : 'divider',
                        '&:hover': { bgcolor: active ? GREEN : '#f0f0f0' }
                      }} />
                  );
                })}
              </Box>
            </Box>
          ))}
          </>
        )}

        {/* 加購（支援 toggle / counter / single_select / multi_select / text） */}
        {Array.isArray(info.addons) && info.addons.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Label>加購</Label>
            <Stack spacing={2}>
              {info.addons.map((a) => {
                const opts = Array.isArray(a.options) ? a.options : [];
                // 需提前 N 天預訂但所選日期太近 → 灰掉 + 提示（不可選）
                const tooSoon = Number.isInteger(a.leadDays) && a.leadDays > 0 && daysUntil < a.leadDays;
                if (tooSoon) {
                  // 太早不可訂：仍列出品項讓客人看得到菜色，但反灰、無勾選框、不可點（勾選框會誘導點擊）
                  return (
                    <Box key={a.key}>
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: opts.length ? 1 : 0 }}>
                        <Typography sx={{ fontSize: 16 }}>{a.label}</Typography>
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.4, borderRadius: 1.5,
                          border: '1px dashed', borderColor: 'divider', bgcolor: '#f6f8fb', color: 'text.secondary', fontSize: 13, whiteSpace: 'nowrap' }}>
                          <AccessTimeIcon sx={{ fontSize: 15 }} /> 需提前 {a.leadDays} 天預訂
                        </Box>
                      </Stack>
                      {opts.length > 0 && (
                        <Stack spacing={1}>
                          {opts.map((o) => (
                            <Box key={o.value} sx={{ px: 1.5, py: 0.9, borderRadius: 2, border: '1px solid',
                              borderColor: 'divider', bgcolor: '#f7f7f7', color: 'text.disabled', fontSize: 16 }}>
                              {o.label}
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  );
                }
                // 數量（＋/−）
                if (a.type === 'counter') {
                  return (
                    <Stack key={a.key} direction="row" justifyContent="space-between" alignItems="center">
                      <Typography sx={{ fontSize: 16 }} color="text.secondary">{a.label}</Typography>
                      <Stepper value={Number(addonValues[a.key]) || 0} min={0} max={a.max || 20} onChange={setAddonNumber(a.key)} />
                    </Stack>
                  );
                }
                // 多選（如：預訂料理）→ 每個 option 一個勾選框
                if (a.type === 'multi_select') {
                  const selected = Array.isArray(addonValues[a.key]) ? addonValues[a.key] : [];
                  return (
                    <Box key={a.key}>
                      <Typography sx={{ fontSize: 16, mb: 0.5 }}>{a.label}</Typography>
                      <Stack spacing={1}>
                        {opts.map((o) => {
                          const on = selected.includes(o.value);
                          return (
                            <FormControlLabel key={o.value}
                              sx={{ m: 0, px: 1.5, py: 0.75, borderRadius: 2, border: '1px solid',
                                borderColor: on ? GREEN : 'divider', bgcolor: on ? 'rgba(6,199,85,0.06)' : '#fff' }}
                              control={<Checkbox checked={on} onChange={toggleMulti(a.key, o.value)} sx={{ '&.Mui-checked': { color: GREEN } }} />}
                              label={<Typography sx={{ fontSize: 16 }}>{o.label}</Typography>} />
                          );
                        })}
                      </Stack>
                    </Box>
                  );
                }
                // 單選 → chip 擇一
                if (a.type === 'single_select') {
                  const val = addonValues[a.key] || '';
                  return (
                    <Box key={a.key}>
                      <Typography sx={{ fontSize: 16, mb: 0.5 }}>{a.label}</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        {opts.map((o) => {
                          const on = val === o.value;
                          return (
                            <Chip key={o.value} label={o.label} onClick={() => setAddonSingle(a.key, on ? '' : o.value)}
                              sx={{
                                height: 46, fontSize: 16, borderRadius: 2, px: 0.5,
                                bgcolor: on ? GREEN : '#fff', color: on ? '#fff' : 'text.primary',
                                border: '1px solid', borderColor: on ? GREEN : 'divider',
                                '&:hover': { bgcolor: on ? GREEN : '#f0f0f0' }
                              }} />
                          );
                        })}
                      </Box>
                    </Box>
                  );
                }
                // 文字
                if (a.type === 'text') {
                  return (
                    <Box key={a.key}>
                      <Typography sx={{ fontSize: 16, mb: 0.5 }}>{a.label}</Typography>
                      <TextField value={addonValues[a.key] || ''} onChange={(e) => setAddonText(a.key, e.target.value)} fullWidth size="small"
                        sx={{ '& .MuiInputBase-input': { fontSize: 16 } }} />
                    </Box>
                  );
                }
                // toggle（預設）
                return (
                  <FormControlLabel key={a.key}
                    control={<Checkbox checked={!!addonValues[a.key]} onChange={(e) => setAddonToggle(a.key, e.target.checked)} sx={{ '&.Mui-checked': { color: GREEN } }} />}
                    label={<Typography sx={{ fontSize: 16 }}>{a.label}</Typography>} />
                );
              })}
            </Stack>
          </Box>
        )}

        {/* 特殊需求（後台自訂標記，多選） */}
        {Array.isArray(info.specialRequestTags) && info.specialRequestTags.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Label>特殊需求</Label>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {info.specialRequestTags.map((tag) => {
                const on = selectedReqs.includes(tag);
                return (
                  <Chip key={tag} label={tag} onClick={() => toggleReq(tag)}
                    sx={{
                      height: 40, fontSize: 16, borderRadius: 2,
                      bgcolor: on ? GREEN : '#fff', color: on ? '#fff' : 'text.primary',
                      border: '1px solid', borderColor: on ? GREEN : 'divider',
                      '&:hover': { bgcolor: on ? GREEN : '#f0f0f0' }
                    }} />
                );
              })}
            </Box>
          </Box>
        )}

        {/* 備註（選填） */}
        <Label sx={{ mt: 3 }}>備註（選填）</Label>
        <TextField value={note} onChange={(e) => setNote(e.target.value)} fullWidth multiline minRows={2}
          placeholder="如有其他需求請告訴我們"
          sx={{ mb: 1, '& .MuiInputBase-input': { fontSize: 16 } }} />

        {/* 聯絡資料 */}
        <Label sx={{ mt: 3 }}>聯絡資料</Label>
        <Stack spacing={2} sx={{ mb: 2 }}>
          {/* 姓名 + 稱謂緊湊分段（預設小姐、可改先生，不強制）→ 稱謂寫入 customer_gender，後台顯示「姓名 先生/小姐」 */}
          <Stack direction="row" spacing={1}>
            <TextField label="姓名" value={name} onChange={(e) => setName(e.target.value)} fullWidth required
              sx={{ '& .MuiInputBase-input': { fontSize: 16 }, '& label': { fontSize: 16 } }} />
            <Stack direction="row" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', flexShrink: 0 }}>
              {['ms', 'mr'].map((g) => {
                const on = gender === g;
                return (
                  <Box key={g} onClick={() => setGender(g)}
                    sx={{
                      px: 2, fontSize: 15, cursor: 'pointer', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap',
                      bgcolor: on ? '#145da0' : 'transparent',
                      color: on ? '#fff' : 'text.secondary',
                      fontWeight: on ? 700 : 400
                    }}>
                    {g === 'ms' ? '小姐' : '先生'}
                  </Box>
                );
              })}
            </Stack>
          </Stack>
          <TextField label="手機號碼" value={phone} onChange={(e) => setPhone(e.target.value)} fullWidth required type="tel" placeholder="0912345678"
            sx={{ '& .MuiInputBase-input': { fontSize: 16 }, '& label': { fontSize: 16 } }} />
          {info.config?.collectEmail && (
            <TextField label="Email（選填）" value={email} onChange={(e) => setEmail(e.target.value)} fullWidth type="email" placeholder="you@example.com"
              sx={{ '& .MuiInputBase-input': { fontSize: 16 }, '& label': { fontSize: 16 } }} />
          )}
        </Stack>

        {/* 同意聲明 */}
        <Typography sx={{ fontSize: 16, color: 'text.secondary', lineHeight: 1.7, mb: 2 }}>
          點選「確認訂位」即表示您同意將上述訂位資訊及姓名、電話等聯絡資料，提供予「{info.restaurant?.name}」及其訂位系統服務商{COMPANY}，作為訂位確認與後續聯繫之用。詳見
          <Link href="https://rebaz.ai/privacy" target="_blank" rel="noopener" sx={{ color: GREEN }}>隱私權政策</Link>。
        </Typography>

        {submitError && <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontSize: 16 }}>{submitError}</Alert>}

        <Button fullWidth variant="contained" disabled={!canSubmit} onClick={handleSubmit}
          sx={{ py: 1.6, bgcolor: GREEN, '&:hover': { bgcolor: GREEN_D }, fontSize: 17 }}>
          {submitting ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : '確認訂位'}
        </Button>
      </Box>
    </Page>
  );
};

const Row = ({ label, value, emphasize }) => (
  <Stack direction="row" justifyContent="space-between">
    <Typography sx={{ fontSize: 16 }} color="text.secondary">{label}</Typography>
    <Typography sx={{ fontSize: 16, fontWeight: emphasize ? 700 : 500, color: emphasize ? GREEN : 'text.primary' }}>{value}</Typography>
  </Stack>
);

export default BookingPage;
